import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, LocateFixed, Crosshair, Compass, Copy, Navigation, Save } from 'lucide-react';
import { crossBearing, cutAngle, readCompass, nv, norm360 } from '../lib/bearing';
import { haversine, uid, compressImage } from '../lib/model';
import { useTrackRec, addWaypoint } from '../lib/trackRec';
import { routeLink } from '../lib/route';

export const openXb = () => window.dispatchEvent(new Event('giss-xb-open'));
const DECL_KEY = 'giss_decl';
const EMPTY = { pt: null, acc: null, brg: '' };
const fmtM = (m) => (m >= 1000 ? `${(m / 1000).toFixed(2).replace('.', ',')} km` : `${Math.round(m)} m`);

function Obs({ t, i, o, set, mapApi }) {
  const gps = () => {
    if (!navigator.geolocation) return toast.error(t.xbNoGps);
    navigator.geolocation.getCurrentPosition((p) => set({ ...o, pt: [p.coords.latitude, p.coords.longitude], acc: p.coords.accuracy }), () => toast.error(t.xbNoGps), { enableHighAccuracy: true, timeout: 15000 });
  };
  const cross = () => { const c = mapApi.current.getCenter(); set({ ...o, pt: [c.lat, c.lng], acc: null }); };
  const comp = async () => { const v = await readCompass(); if (v == null) toast.error(t.xbNoCompass); else set({ ...o, brg: String(v) }); };
  return (
    <div className="xb-obs" data-testid={`xb-obs-${i}`}>
      <b>📍 {t.xbObs} P{i}</b>
      <div className="row">
        <button className="btn btn-outline btn-sm" data-testid={`xb-gps-${i}`} onClick={gps}><LocateFixed size={14} /> GPS</button>
        <button className="btn btn-outline btn-sm" data-testid={`xb-cross-${i}`} onClick={cross}><Crosshair size={14} /> {t.xbCross}</button>
      </div>
      <small data-testid={`xb-pos-${i}`}>{o.pt ? `${o.pt[0].toFixed(6)}, ${o.pt[1].toFixed(6)}${o.acc ? ` · ±${Math.round(o.acc)} m` : ''}` : '–'}</small>
      <label>{t.xbBrg}<input inputMode="decimal" data-testid={`xb-brg-${i}`} value={o.brg} placeholder="0–360" onChange={(e) => set({ ...o, brg: e.target.value })} /></label>
      <button className="btn btn-dark btn-sm" data-testid={`xb-compass-${i}`} onClick={comp}><Compass size={14} /> {t.xbCompass}</button>
    </div>
  );
}

function Result({ t, a, b, tgt, ang, onSave }) {
  const weak = ang < 30 || ang > 150;
  const copy = () => navigator.clipboard?.writeText(`${tgt[0].toFixed(7)}, ${tgt[1].toFixed(7)}`).then(() => toast.success(t.shCopied));
  return (
    <div className="xb-res" data-testid="xb-result">
      <b>🎯 {t.xbResult} P3: <span data-testid="xb-target">{tgt[0].toFixed(6)}, {tgt[1].toFixed(6)}</span></b>
      <small data-testid="xb-dists">{t.xbFrom} P1 {fmtM(haversine(a.pt, tgt))} · P2 {fmtM(haversine(b.pt, tgt))} · {t.xbAngle} {Math.round(ang)}°</small>
      {weak && <small className="xb-weak" data-testid="xb-weak">⚠️ {t.xbWeak}</small>}
      <div className="row">
        <button className="btn btn-outline btn-sm" data-testid="xb-copy-btn" onClick={copy}><Copy size={14} /> {t.xbCopy}</button>
        <button className="btn btn-dark btn-sm" data-testid="xb-save-btn" onClick={onSave}><Save size={14} /> {t.xbSave}</button>
        <a className="btn btn-green btn-sm" data-testid="xb-nav-btn" href={routeLink({ lat: tgt[0], lon: tgt[1] })} target="_blank" rel="noreferrer"><Navigation size={14} /> {t.alNav}</a>
      </div>
    </div>
  );
}

// two observer positions + two compass bearings -> target position (forestry / outdoor / survey)
export default function CrossBearing({ t, mapApi, project, updateProject, repId }) {
  const rec = useTrackRec();
  const [open, setOpen] = useState(false);
  const [a, setA] = useState(EMPTY);
  const [b, setB] = useState(EMPTY);
  const [decl, setDecl] = useState(() => localStorage.getItem(DECL_KEY) || '');
  const [photo, setPhoto] = useState(null);
  useEffect(() => { const on = () => setOpen(true); window.addEventListener('giss-xb-open', on); return () => window.removeEventListener('giss-xb-open', on); }, []);
  const d = nv(decl) || 0, b1 = nv(a.brg), b2 = nv(b.brg);
  const ready = a.pt && b.pt && b1 != null && b2 != null;
  const tgt = ready ? crossBearing(a.pt, norm360(b1 + d), b.pt, norm360(b2 + d)) : null;
  useEffect(() => { if (open) mapApi.current?.svyMarks([a.pt, b.pt, tgt].filter(Boolean)); }, [open, a.pt, b.pt, tgt?.[0], tgt?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const close = () => { setOpen(false); mapApi.current?.svyMarks([]); };
  const show = () => { mapApi.current.fitAll([a.pt, b.pt, tgt]); };
  const save = () => {
    const ang = cutAngle(b1, b2), n = (project.fittings || []).filter((x) => x.type === 'xbear').length + 1;
    const f = { id: uid('fit'), type: 'xbear', state: 'none', name: `${t.fit_xbear} ${n}`, note: '', lat: tgt[0], lon: tgt[1], media: [], memos: [], reportId: repId,
      xb: { p1: a.pt, p2: b.pt, b1, b2, decl: d, ang, d1: haversine(a.pt, tgt), d2: haversine(b.pt, tgt), t: Date.now(), ...(photo ? { photo } : {}) } };
    if (rec && rec.status !== 'done') { addWaypoint({ id: uid('wp'), kind: 'note', status: null, note: `🧭 ${f.name}`, lat: tgt[0], lon: tgt[1], alt: null, acc: null, t: Date.now(), photos: 0, videos: 0, memos: 0, fid: f.id }); f.trackRef = `R${rec.no || 1}`; }
    updateProject((p) => ({ ...p, fittings: [...(p.fittings || []), f] }));
    toast.success(`🧭 ${f.name} ${t.xbSaved}${f.trackRef ? ` · ${t.xbWp}` : ''}`);
    setA(EMPTY); setB(EMPTY); setPhoto(null); close();
  };
  return (
    <div className="wp-sheet xb-sheet" data-testid="xb-sheet">
      <div className="wp-head"><b>🧭 {t.xbTitle}</b><button className="icon-btn" data-testid="xb-close-btn" onClick={close}><X size={18} /></button></div>
      <small className="muted">{t.xbHint}</small>
      <div className="xb-grid">
        <Obs t={t} i={1} o={a} set={setA} mapApi={mapApi} />
        <Obs t={t} i={2} o={b} set={setB} mapApi={mapApi} />
      </div>
      <label className="xb-decl">{t.xbDecl}<input inputMode="decimal" data-testid="xb-decl" value={decl} placeholder="0" onChange={(e) => { setDecl(e.target.value); localStorage.setItem(DECL_KEY, e.target.value); }} /></label>
      {!ready && <small data-testid="xb-need">{t.xbNeed}</small>}
      {ready && !tgt && <small className="xb-weak" data-testid="xb-parallel">⚠️ {t.xbParallel}</small>}
      {tgt && <Result t={t} a={a} b={b} tgt={tgt} ang={cutAngle(b1, b2)} onSave={save} />}
      {tgt && (
        <div className="row">
          <label className="btn btn-outline btn-sm">📷 {photo ? t.xbPhoto : t.xbPhotoAdd}<input hidden type="file" accept="image/*" capture="environment" data-testid="xb-photo-input" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setPhoto((await compressImage(f, 1200, 0.75)).data); }} /></label>
          {photo && <img src={photo} alt="" className="ds-ph" data-testid="xb-photo-thumb" />}
        </div>
      )}
      {tgt && <button className="btn btn-green" data-testid="xb-show-btn" onClick={show}>🎯 {t.xbCenter}</button>}
    </div>
  );
}
